import z from 'zod';

// ─── Editor-defined form fields ─────────────────────────────────────────────
// The "NVISO - Custom Contact Form" LazyBlock lets editors define their own
// fields through a repeater control. Those raw definitions are normalised here
// and turned into (a) a zod schema for client-side validation and (b) a row
// layout for the GenericForm config.

export const customFieldTypes = [
  'text',
  'email',
  'tel',
  'number',
  'date',
  'textarea',
  'select',
  'radio',
  'checkbox',
] as const;

export type CustomFieldType = (typeof customFieldTypes)[number];

/** Shape as it arrives from the LazyBlocks repeater (all values are loose). */
export type RawCustomField = {
  name?: string | null
  label?: string | null
  type?: string | null
  placeholder?: string | null
  help?: string | null
  options?: string | null
  required?: boolean | string | null
  width?: string | null
};

export type NormalizedField = {
  name: string
  label: string
  type: CustomFieldType
  placeholder?: string
  help?: string
  required: boolean
  halfWidth: boolean
  options: { label: string, value: string }[]
};

export type ValidationMessages = {
  required: string
  email: string
  number: string
  consent: string
};

export const defaultValidationMessages: ValidationMessages = {
  required: 'This field is required',
  email: 'Please enter a valid email address',
  number: 'Please enter a valid number',
  consent: 'Please accept this to continue',
};

/** LazyBlocks toggles can serialise as booleans or as "true"/"false" strings. */
export function toBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    if (value === '') return fallback;
    return value !== 'false' && value !== '0';
  }
  if (typeof value === 'number') return value !== 0;
  return fallback;
}

export function slugify(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/**
 * Options are entered as one option per line, either as `value|Label` or just
 * `Label` (in which case the label doubles as the value).
 */
export function parseOptions(raw?: string | null): { label: string, value: string }[] {
  if (!raw) return [];
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const separatorIndex = line.indexOf('|');
      if (separatorIndex === -1) {
        return { label: line, value: line };
      }
      return {
        value: line.slice(0, separatorIndex).trim(),
        label: line.slice(separatorIndex + 1).trim() || line.slice(0, separatorIndex).trim(),
      };
    });
}

function resolveType(type?: string | null): CustomFieldType {
  const normalized = (type ?? 'text').trim().toLowerCase();
  return (customFieldTypes as readonly string[]).includes(normalized)
    ? normalized as CustomFieldType
    : 'text';
}

/**
 * Normalises raw repeater rows: resolves types, derives missing names from the
 * label and guarantees unique field names (duplicates get a numeric suffix).
 */
export function normalizeFields(raw?: RawCustomField[] | null): NormalizedField[] {
  if (!Array.isArray(raw)) return [];

  const usedNames = new Set<string>();

  return raw.reduce<NormalizedField[]>((acc, entry, index) => {
    const label = entry?.label?.trim() ?? '';
    const explicitName = entry?.name?.trim() ?? '';
    if (!label && !explicitName) return acc;

    const baseName = slugify(explicitName || label) || `field-${index + 1}`;
    let name = baseName;
    let suffix = 2;
    while (usedNames.has(name)) {
      name = `${baseName}-${suffix}`;
      suffix += 1;
    }
    usedNames.add(name);

    const type = resolveType(entry?.type);
    const options = parseOptions(entry?.options);

    // A select/radio without options would render an unusable control.
    if ((type === 'select' || type === 'radio') && options.length === 0) return acc;

    acc.push({
      name,
      label: label || explicitName,
      type,
      placeholder: entry?.placeholder?.trim() || undefined,
      help: entry?.help?.trim() || undefined,
      required: toBoolean(entry?.required),
      halfWidth: entry?.width === 'half',
      options,
    });

    return acc;
  }, []);
}

function buildFieldValidator(field: NormalizedField, messages: ValidationMessages) {
  if (field.type === 'checkbox') {
    return field.required
      ? z.literal(true, { errorMap: () => ({ message: messages.required }) })
      : z.boolean().optional().default(false);
  }

  if (field.type === 'number') {
    const base = z.string().trim().regex(/^-?\d+([.,]\d+)?$/, messages.number);
    return field.required ? base : z.union([base, z.literal('')]).optional();
  }

  if (field.type === 'email') {
    const base = z.string().trim().email(messages.email);
    return field.required ? base : z.union([base, z.literal('')]).optional();
  }

  if (field.type === 'select' || field.type === 'radio') {
    const values = field.options.map((option) => option.value);
    const base = z.string().refine(
      (value) => values.includes(value),
      { message: messages.required },
    );
    return field.required ? base : z.union([base, z.literal('')]).optional();
  }

  const base = z.string().trim();
  return field.required ? base.min(1, messages.required) : base.optional();
}

export type BuildSchemaOptions = {
  consentRequired: boolean
  requireTurnstile: boolean
  messages?: Partial<ValidationMessages>
};

/** Builds a zod object schema matching the editor-defined fields. */
export function buildFormSchema(
  fields: NormalizedField[],
  { consentRequired, requireTurnstile, messages }: BuildSchemaOptions,
) {
  const resolvedMessages = { ...defaultValidationMessages, ...messages };

  const shape = fields.reduce<Record<string, z.ZodTypeAny>>((acc, field) => {
    acc[field.name] = buildFieldValidator(field, resolvedMessages);
    return acc;
  }, {});

  shape.$consent = consentRequired
    ? z.literal(true, { errorMap: () => ({ message: resolvedMessages.consent }) })
    : z.boolean().optional().default(false);

  shape.$turnstile_key = requireTurnstile
    ? z.string().min(1, resolvedMessages.required)
    : z.string().optional().default('disabled');

  // Honeypot: bots fill every input they find, humans never see this one.
  // Not validated client-side on purpose — the server silently drops it.
  shape.$website = z.string().optional();

  return z.object(shape);
}

/** Groups fields into rows so that two consecutive half-width fields share a row. */
export function buildFieldRows(fields: NormalizedField[]): NormalizedField[][] {
  return fields.reduce<NormalizedField[][]>((rows, field) => {
    const lastRow = rows[rows.length - 1];
    if (
      field.halfWidth
      && lastRow?.length === 1
      && lastRow[0].halfWidth
    ) {
      lastRow.push(field);
      return rows;
    }
    rows.push([field]);
    return rows;
  }, []);
}

/** Maps our field types onto the GenericForm field types. */
export function toGenericFormType(type: CustomFieldType) {
  return type === 'text' ? 'input' : type;
}
